import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { getActiveWorldConfig, type LocaleDef, type MessageCatalog } from '@gamecodex/world-config'

/**
 * Active-locale context. Mounted by the `/:locale` route (see router.tsx). The
 * locale comes from the URL segment; an unsupported/missing segment redirects to
 * the world's default locale, preserving the rest of the path.
 */
interface LocaleContextValue {
  /** Active locale code, e.g. `en`. */
  locale: string
  localeDef: LocaleDef
  dir: 'ltr' | 'rtl'
  /** Active locale's catalog. */
  catalog: MessageCatalog
  /** Default locale's catalog (fallback for missing keys). */
  defaultCatalog: MessageCatalog
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

/** Build a context value for a locale code from the active world config. */
function valueFor(code: string): LocaleContextValue {
  const cfg = getActiveWorldConfig()
  const { default: defaultCode, supported } = cfg.locales
  const active = supported.find((l) => l.code === code) ?? supported.find((l) => l.code === defaultCode) ?? supported[0]
  return {
    locale: active.code,
    localeDef: active,
    dir: active.dir,
    catalog: cfg.content[active.code] ?? {},
    defaultCatalog: cfg.content[defaultCode] ?? {},
  }
}

/**
 * Active locale + catalog. Inside the `/:locale` route it reflects the URL; with
 * NO provider (an isolated component test, or the bare `/overlay` route) it falls
 * back to the world's default locale rather than throwing — so `t()` / `LocaleLink`
 * are safe to use anywhere.
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  return ctx ?? valueFor(getActiveWorldConfig().locales.default)
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { locale: param } = useParams()
  const location = useLocation()
  const cfg = getActiveWorldConfig()
  const { default: defaultCode, supported } = cfg.locales
  const match = supported.find((l) => l.code === param)

  // Hooks must run unconditionally — compute value even when we will redirect.
  const value = useMemo<LocaleContextValue>(() => valueFor(match?.code ?? defaultCode), [match, defaultCode])

  if (!match) {
    // The first segment isn't a supported locale → treat the whole path as a
    // pre-i18n (unprefixed) URL and prepend the default locale, so old deep links
    // like `/encyclopedia` become `/en/encyclopedia` (not lost). A genuinely bad
    // locale like `/xx/foo` becomes `/en/xx/foo` → the child 404 (acceptable, rare).
    return <Navigate to={`/${defaultCode}${location.pathname}${location.search}${location.hash}`} replace />
  }

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}
